import { z } from "zod";
import { FLOOR_HEIGHT_M } from "./constants.js";
/**
 * Part ids are stable strings: "roof", "f2.wall.n", "f2.slab", "body", "mesh:<gltf node name>".
 * The charset keeps them safe to put into an agent prompt. Same rule as `FocusPartSchema` in
 * `@uzverse/core`.
 */
export const PartIdSchema = z
    .string()
    .max(64)
    .regex(/^[a-z0-9]+(?:[.:][a-z0-9_-]+)*$/i, "must be a part id like 'f2.wall.n'");
export const WALL_SIDES = ["n", "e", "s", "w"];
export const ROOF_PART = "roof";
export const BODY_PART = "body";
export const slabPart = (level) => `f${level}.slab`;
export const wallPart = (level, side) => `f${level}.wall.${side}`;
/**
 * Floor bands of a building, lowest first. Basements (level < 0) are not rendered above ground
 * and have no parts. Duplicate levels collapse.
 */
export function buildingParts(floors) {
    const levels = [...new Set(floors.map((f) => f.level).filter((l) => l >= 0))].sort((a, b) => a - b);
    return levels.map((level) => ({
        level,
        y: level * FLOOR_HEIGHT_M,
        height: FLOOR_HEIGHT_M,
        slab: slabPart(level),
        walls: WALL_SIDES.map((side) => wallPart(level, side)),
    }));
}
/** Every part id of a building: floors (slab + 4 walls) then the roof. */
export function allBuildingPartIds(floors) {
    return [...buildingParts(floors).flatMap((b) => [b.slab, ...b.walls]), ROOF_PART];
}
/** Only the wall parts of a building (what "paint the walls" means). */
export function wallPartIds(floors) {
    return buildingParts(floors).flatMap((b) => b.walls);
}
/** Height in meters of a building with these floors (at least one floor tall). */
export function buildingHeight(floors) {
    const bands = buildingParts(floors);
    const top = bands.length === 0 ? 0 : bands[bands.length - 1].level + 1;
    return Math.max(1, top) * FLOOR_HEIGHT_M;
}
//# sourceMappingURL=parts.js.map